import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { UserService } from '../../auth/services/user.service';
import { CreditBlockedModalComponent, CreditBlockedModalData } from '../components/credit-blocked-modal/credit-blocked-modal.component';

/**
 * Interceptor global: cuando el backend devuelve 423 Locked (acceso bloqueado por
 * mora del módulo Créditos), abre el modal de bloqueo y suprime el error toast
 * por defecto. El payload del backend viene en `error.error.data` con la estructura
 * de `CreditBlockedModalData`.
 *
 * Reglas importantes:
 *   - El modal SOLO se abre cuando hay un usuario autenticado. En la pantalla de
 *     login (sin token) un 423 no debe disparar el modal porque "Notificar pago"
 *     no podría completarse sin sesión activa.
 *   - Si el usuario cierra sesión mientras el modal está abierto, lo descartamos
 *     automáticamente para no quedar atrapado en una UI sin contexto.
 *   - Idempotente: si el modal ya está abierto, no se reabre.
 */
@Injectable()
export class CreditBlockedInterceptor implements HttpInterceptor {
    private modalRef: NgbModalRef | null = null;

    constructor(private modalService: NgbModal, private userService: UserService) {
        // Cierra el modal si el usuario sale de sesión.
        this.userService.user$.subscribe(user => {
            if (!user && this.modalRef) {
                try {
                    this.modalRef.dismiss('logout');
                } catch {
                    /* noop */
                }
                this.modalRef = null;
            }
        });
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            catchError((err: HttpErrorResponse) => {
                // Solo tratamos 423 que vengan del middleware `requireCreditAccess` del API
                // (identificable por `data.blocked === true` en el payload). Si en el futuro
                // otro módulo del backend o un servicio externo (S3, CDN, rate-limit) devuelve
                // 423, NO debemos abrir el modal de mora ni tragar el error — lo propagamos
                // como cualquier otro fallo. Defensa en profundidad para evitar UX rota por
                // colisión de status codes con otras features.
                if (err?.status === 423 && err?.error?.data?.blocked === true) {
                    const opened = this.tryOpenBlockedModal(err.error?.data);
                    // Si el modal abrió correctamente, tragamos el error para no
                    // propagarlo a toasts globales o handlers de error de los componentes.
                    // Si NO abrió (no hay sesión), dejamos pasar el error normal.
                    if (opened) return EMPTY;
                }
                return throwError(() => err);
            }),
        );
    }

    /**
     * Abre el modal si:
     *   - hay datos en el payload del 423
     *   - todavía no hay otro modal abierto
     *   - hay usuario autenticado (sin token "Notificar pago" no funcionaría)
     * Devuelve `true` si abrió (o ya estaba abierto) — el caller usa eso para decidir
     * si tragar el 423 (no propagar a otros handlers).
     */
    private tryOpenBlockedModal(data: CreditBlockedModalData | undefined): boolean {
        if (this.modalRef) return true; // ya hay uno → tragamos también el nuevo 423
        if (!data) return false;
        const user = this.userService.user$.getValue();
        if (!user) return false; // pre-login: dejá pasar el error como cualquier otro

        this.modalRef = this.modalService.open(CreditBlockedModalComponent, {
            centered: true,
            backdrop: 'static',
            keyboard: false,
            size: 'md',
            // Ancla el ngb-modal-window al viewport (ver credit-blocked-modal.component.scss):
            // el modal queda SIEMPRE centrado en pantalla aunque el usuario haga scroll.
            windowClass: 'cb-modal-window',
        });
        const inst = this.modalRef.componentInstance as CreditBlockedModalComponent;
        inst.data = data;
        // FINA-2062: si el user logueado NO es owner del tenant, el modal entra en modo viewer:
        // muestra info de la deuda pero deshabilita el formulario de pago. Backend también rechaza
        // PUT /api/creditos/:id/pay con 403 para non-owner.
        inst.viewerMode = !user.isOwner;
        this.modalRef.result.finally(() => {
            this.modalRef = null;
        });
        return true;
    }
}
